import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, Trash2, ChevronDown, SlidersHorizontal, Flag, CircleDot, UserRound } from "lucide-react";
import SharedStatsGrid from "../../../../components/StatsGrid";
import { CheckCircle, Clock, AlertCircle, ListChecks } from "lucide-react";

import useTasks from "../../hooks/useTasks";
import useAssignedByMe from "../../hooks/useAssignedByMe";
import useDirectReports from "../../hooks/useDirectReports";
import useTaskHistory from "../../hooks/useTaskHistory";
import TaskFormModal from "./TaskFormModal";
import TaskDetailModal from "./TaskDetailModal";
import ReusableDropdown from "../../../../components/ReusableDropdown";
import AlertModal from "../../../../components/AlertModal";

const PRIORITY_COLORS = { LOW: "#22c55e", NORMAL: "#2e7cba", HIGH: "#f59e0b", URGENT: "#ef4444" };
const STATUS_COLORS = {
  PENDING: "#94a3b8",
  IN_PROGRESS: "#2e7cba",
  COMPLETED: "#22c55e",
  CANCELLED: "#ef4444",
};
const STATUS_LABEL_KEY = {
  PENDING: "statusPending",
  IN_PROGRESS: "statusInProgress",
  COMPLETED: "statusCompleted",
  CANCELLED: "statusCancelled",
};

const isOverdue = (task) =>
  !!task.dueDate &&
  new Date(task.dueDate).getTime() < Date.now() &&
  !["COMPLETED", "CANCELLED"].includes(task.status);

// Due-date urgency color tiers — identical thresholds to the mobile app
// (green >7d, yellow 3-7d, orange 1-2d, red overdue), so a manager sees the
// same urgency signal regardless of device (plan §4 / §7).
const dueDateColor = (task) => {
  if (!task.dueDate) return null;
  if (isOverdue(task)) return "#ef4444";
  const days = Math.ceil((new Date(task.dueDate).getTime() - Date.now()) / 86400000);
  if (days <= 2) return "#f97316";
  if (days <= 7) return "#eab308";
  return "#22c55e";
};

// A labeled, icon-prefixed filter dropdown that visually highlights when
// it's been changed from its default ("all") — replaces the previous bare
// <select> row that had no label, no icon, and no way to tell an active
// filter apart from an unset one at a glance.
const FilterChipGroup = ({ icon: Icon, label, value, onChange, active, options, styles, language, theme }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160, flex: "1 1 160px" }}>
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        opacity: 0.75,
        color: styles.formLabel.color,
      }}
    >
      <Icon size={13} />
      {label}
    </span>
    <ReusableDropdown
      items={options}
      value={value}
      onChange={onChange}
      getItemKey={(opt) => opt.value}
      getItemLabel={(opt) => opt.label}
      language={language}
      theme={theme}
      inputStyle={active ? { border: "2px solid #2e7cba", fontWeight: 700 } : { fontWeight: 500 }}
    />
  </div>
);

// Replaces the old small SimpleTodoList widget — full section with tabs,
// stats, filters, and a table/card list, matching the visual weight of
// (Admin)EmployeeActions rather than the previous compact checkbox widget.
// See WEB_TASK_ASSIGNMENT_UI_PLAN.md for the full design rationale.
const TasksSection = ({
  BASE_URL,
  currentInfinityCode,
  clientName,
  hasFeatureAccess,
  styles,
  screenSize,
  t,
  language,
  theme,
  focusTaskId,
}) => {
  const [activeTab, setActiveTab] = useState("mine"); // "mine" | "assignedByMe"
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all"); // all | personal | assignedToMe (mine tab only)

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  const [alertModal, setAlertModal] = useState({ open: false, title: "", message: "", variant: "error" });
  const showAlert = (title, message, variant = "error") =>
    setAlertModal({ open: true, title, message, variant });
  const closeAlert = () => setAlertModal((prev) => ({ ...prev, open: false }));

  const canAssign = hasFeatureAccess("calendar", "assignTasks");

  const myTasksHook = useTasks(BASE_URL, currentInfinityCode);
  const assignedByMeHook = useAssignedByMe(BASE_URL, currentInfinityCode);
  const directReportsHook = useDirectReports(BASE_URL, clientName, currentInfinityCode, language);
  const historyHook = useTaskHistory(BASE_URL);

  const hasDirectReports = canAssign && directReportsHook.directReports.length > 0;

  // The task record only ever carries the assignee's CODE (infinityCode),
  // never a display name — resolved here from the already-fetched direct
  // reports list (the same data source AssigneePicker uses) so both the row
  // list and the detail modal can actually show who a task was assigned to,
  // not just who assigned it.
  const directReportsByCode = useMemo(() => {
    const map = {};
    for (const emp of directReportsHook.directReports) map[String(emp.infinityCode)] = emp.name;
    return map;
  }, [directReportsHook.directReports]);
  const assigneeNameFor = (task) =>
    directReportsByCode[String(task.infinityCode)] || task.infinityCode;

  // Notification deep-link (?todoId=...) — mirrors the web app's existing
  // convention (?leaveId=, ?documentId=) of auto-opening the relevant record
  // once its data has loaded (plan's web UI §8). Consumed once so it doesn't
  // reopen the modal every time the underlying list refetches.
  const focusConsumedRef = useRef(false);
  useEffect(() => {
    if (!focusTaskId || focusConsumedRef.current) return;
    const inMine = myTasksHook.tasks.find((tk) => String(tk.id) === String(focusTaskId));
    const inAssigned = assignedByMeHook.tasks.find((tk) => String(tk.id) === String(focusTaskId));
    const found = inMine || inAssigned;
    if (found) {
      focusConsumedRef.current = true;
      setActiveTab(inMine ? "mine" : "assignedByMe");
      setDetailTask(found);
    }
  }, [focusTaskId, myTasksHook.tasks, assignedByMeHook.tasks]);

  const activeList = activeTab === "mine" ? myTasksHook.tasks : assignedByMeHook.tasks;

  const filteredTasks = useMemo(() => {
    return activeList.filter((task) => {
      if (statusFilter !== "all" && task.status !== statusFilter) return false;
      if (priorityFilter !== "all" && task.priority !== priorityFilter) return false;
      if (activeTab === "mine") {
        if (scopeFilter === "personal" && task.assignedBy) return false;
        if (scopeFilter === "assignedToMe" && !task.assignedBy) return false;
      }
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const hay = `${task.title || ""} ${task.description || ""} ${task.assignedByName || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [activeList, statusFilter, priorityFilter, scopeFilter, activeTab, search]);

  const stats = useMemo(() => {
    const source = activeTab === "assignedByMe" ? assignedByMeHook.tasks : myTasksHook.tasks;
    const total = source.length;
    const completed = source.filter((tk) => tk.status === "COMPLETED").length;
    const inProgress = source.filter((tk) => tk.status === "IN_PROGRESS").length;
    const overdue = source.filter(isOverdue).length;
    return [
      { title: t.taskTotal, value: total, icon: ListChecks, iconBackground: "#2e7cba" },
      { title: t.taskCompletedCard, value: completed, icon: CheckCircle, iconBackground: "#22c55e" },
      { title: t.taskInProgress, value: inProgress, icon: Clock, iconBackground: "#eab308" },
      { title: t.taskOverdue, value: overdue, icon: AlertCircle, iconBackground: "#ef4444" },
    ];
  }, [activeTab, assignedByMeHook.tasks, myTasksHook.tasks, t]);

  const openCreate = () => {
    setEditingTask(null);
    setShowFormModal(true);
  };
  const openEdit = (task) => {
    setDetailTask(null);
    setEditingTask(task);
    setShowFormModal(true);
  };

  const handleFormSubmit = async (payload) => {
    if (editingTask) {
      await myTasksHook.updateTask(editingTask.id, payload);
      assignedByMeHook.patchLocal(editingTask.id, payload);
    } else {
      await myTasksHook.addTask(payload);
      if (payload.assignedTo) assignedByMeHook.refetch();
    }
  };

  const handleStart = async (task) => {
    try {
      const updated = await myTasksHook.setTaskStatus(task.id, "IN_PROGRESS");
      setDetailTask(updated);
    } catch (err) {
      showAlert(language === "ar" ? "تعذّر تحديث الحالة" : "Cannot Update Status", err.message || String(err));
    }
  };
  const handleComplete = async (task) => {
    try {
      const updated = await myTasksHook.setTaskStatus(task.id, "COMPLETED");
      setDetailTask(updated);
      assignedByMeHook.patchLocal(task.id, updated);
    } catch (err) {
      showAlert(language === "ar" ? "تعذّر تحديث الحالة" : "Cannot Update Status", err.message || String(err));
    }
  };
  const handleCancelTask = async (task) => {
    try {
      await myTasksHook.updateTask(task.id, { status: "CANCELLED" });
      assignedByMeHook.patchLocal(task.id, { status: "CANCELLED" });
      setDetailTask(null);
    } catch (err) {
      showAlert(language === "ar" ? "تعذّر الإلغاء" : "Cannot Cancel Task", err.message || String(err));
    }
  };
  const handleDelete = async (task) => {
    if (!window.confirm(t.deleteTask + "?")) return;
    try {
      await myTasksHook.deleteTask(task.id);
    } catch (err) {
      showAlert(language === "ar" ? "تعذّر الحذف" : "Cannot Delete Task", err.message || String(err));
    }
  };

  // Quick complete/reopen checkbox — only ever shown for a row where the
  // CURRENT USER is the owner (personal task, or a task assigned TO them),
  // never for a task they merely assigned to someone else: per the backend's
  // own ownership split, only the owner may decide when a task is done —
  // the assigner can edit/cancel it, but can't tick it complete for them.
  const [togglingId, setTogglingId] = useState(null);
  const handleQuickToggle = async (task) => {
    if (togglingId) return;
    setTogglingId(task.id);
    try {
      const nextStatus = task.status === "COMPLETED" ? "PENDING" : "COMPLETED";
      const updated = await myTasksHook.setTaskStatus(task.id, nextStatus);
      assignedByMeHook.patchLocal(task.id, updated);
    } catch (err) {
      showAlert(language === "ar" ? "تعذّر تحديث الحالة" : "Cannot Update Status", err.message || String(err));
    } finally {
      setTogglingId(null);
    }
  };

  const isRTL = language === "ar";
  const isMobile = screenSize.isMobile;

  const renderRow = (task) => {
    const dueColor = dueDateColor(task);
    return (
      <div
        key={task.id}
        onClick={() => setDetailTask(task)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: isMobile ? 14 : 16,
          borderRadius: 14,
          marginBottom: 8,
          cursor: "pointer",
          background: styles.controlPanel.background,
          border: styles.controlPanel.border,
          transition: "all 0.2s ease",
        }}
      >
        {String(task.infinityCode) === String(currentInfinityCode) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleQuickToggle(task); }}
            disabled={togglingId === task.id}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: `2px solid ${task.status === "COMPLETED" ? "#22c55e" : "rgba(148,163,184,0.5)"}`,
              background: task.status === "COMPLETED" ? "#22c55e" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              cursor: togglingId === task.id ? "not-allowed" : "pointer",
              opacity: togglingId === task.id ? 0.5 : 1,
              transition: "all 0.2s ease",
              padding: 0,
            }}
          >
            {task.status === "COMPLETED" && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
        )}
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: PRIORITY_COLORS[task.priority],
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              color: styles.formLabel.color,
              textDecoration: task.status === "COMPLETED" ? "line-through" : "none",
              opacity: task.status === "COMPLETED" ? 0.6 : 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {task.title}
          </div>
          {task.assignedBy && (
            <div style={{ fontSize: 12, opacity: 0.7, display: "flex", alignItems: "center", gap: 4 }}>
              <UserRound size={12} />
              {activeTab === "mine"
                ? `${t.assignedBy}: ${task.assignedByName || task.assignedBy}`
                : `${t.assignTo}: ${assigneeNameFor(task)}`}
            </div>
          )}
        </div>

        {!isMobile && (
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              padding: "4px 10px",
              borderRadius: 20,
              color: STATUS_COLORS[task.status],
              border: `1.5px solid ${STATUS_COLORS[task.status]}`,
              flexShrink: 0,
            }}
          >
            {t[STATUS_LABEL_KEY[task.status]]}
          </span>
        )}

        {task.dueDate && (
          <span style={{ fontSize: 12, fontWeight: 600, color: dueColor, flexShrink: 0 }}>
            {new Date(task.dueDate).toLocaleDateString(isRTL ? "ar-EG" : "en-GB", {
              day: "numeric",
              month: "short",
            })}
          </span>
        )}

        {!task.assignedBy && String(task.infinityCode) === String(currentInfinityCode) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(task);
            }}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#ef4444", flexShrink: 0 }}
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={styles.controlPanel}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
        <h3 style={{ ...styles.modalTitle, fontSize: isMobile ? 18 : 22, margin: 0 }}>{t.tasks}</h3>
        <button type="button" style={styles.actionButton("primary", "small")} onClick={openCreate}>
          <Plus size={16} /> {t.newTask}
        </button>
      </div>

      {/* Tab switcher only makes sense once there's a second tab to switch
          to — a plain self-service user (no direct reports, canAssign
          false) only ever has "My Tasks", so showing a single active-looking
          "My Tasks" button with nothing to switch away from is just noise;
          skip the whole strip for that account type. */}
      {hasDirectReports && (
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          <button
            type="button"
            style={styles.viewModeButton(activeTab === "mine")}
            onClick={() => setActiveTab("mine")}
          >
            {t.myTasks}
          </button>
          <button
            type="button"
            style={styles.viewModeButton(activeTab === "assignedByMe")}
            onClick={() => setActiveTab("assignedByMe")}
          >
            {t.assignedByMe}
          </button>
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <SharedStatsGrid
          styles={{
            ...styles,
            statHeader: { display: "flex", alignItems: "center", gap: 8, ...(styles.statHeader || {}) }
          }}
          stats={stats}
          screenSize={screenSize}
          gridStyle={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(150px, 100%), 1fr))" }}
        />
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ ...styles.searchContainer, marginBottom: 12 }}>
          <Search
            size={18}
            style={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              [isRTL ? "right" : "left"]: 16,
              opacity: 0.6,
            }}
          />
          <input
            style={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.searchTasks}
          />
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
          <FilterChipGroup
            icon={CircleDot}
            label={t.filterStatus}
            value={statusFilter}
            onChange={setStatusFilter}
            active={statusFilter !== "all"}
            styles={styles}
            language={language}
            theme={theme}
            options={[
              { value: "all", label: t.allStatuses },
              { value: "PENDING", label: t.statusPending },
              { value: "IN_PROGRESS", label: t.statusInProgress },
              { value: "COMPLETED", label: t.statusCompleted },
              { value: "CANCELLED", label: t.statusCancelled },
            ]}
          />

          <FilterChipGroup
            icon={Flag}
            label={t.filterPriority}
            value={priorityFilter}
            onChange={setPriorityFilter}
            active={priorityFilter !== "all"}
            styles={styles}
            language={language}
            theme={theme}
            options={[
              { value: "all", label: t.allPriorities },
              { value: "LOW", label: t.priorityLow },
              { value: "NORMAL", label: t.priorityNormal },
              { value: "HIGH", label: t.priorityHigh },
            ]}
          />

          {activeTab === "mine" && (
            <FilterChipGroup
              icon={SlidersHorizontal}
              label={t.filterAssignedBy}
              value={scopeFilter}
              onChange={setScopeFilter}
              active={scopeFilter !== "all"}
              styles={styles}
              language={language}
              theme={theme}
              options={[
                { value: "all", label: t.allAssigners },
                { value: "personal", label: t.personalOnly },
                { value: "assignedToMe", label: t.assignedToMeOnly },
              ]}
            />
          )}
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyDescription}>{t.noTasksFound}</p>
        </div>
      ) : (
        // Capped to ~4 rows tall with its own scrollbar past that — creating
        // more tasks should never keep growing the page itself.
        <div style={{ maxHeight: "340px", overflowY: "auto", paddingRight: "4px" }}>
          {filteredTasks.map(renderRow)}
        </div>
      )}

      {activeTab === "assignedByMe" && assignedByMeHook.hasMore && (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <button
            type="button"
            style={styles.actionButton("default", "small")}
            onClick={assignedByMeHook.loadMore}
            disabled={assignedByMeHook.loading}
          >
            {t.loadMore} <ChevronDown size={14} />
          </button>
        </div>
      )}

      <TaskFormModal
        show={showFormModal}
        task={editingTask}
        canAssign={canAssign}
        directReports={directReportsHook.directReports}
        styles={styles}
        screenSize={screenSize}
        t={t}
        language={language}
        onClose={() => setShowFormModal(false)}
        onSubmit={handleFormSubmit}
      />

      <TaskDetailModal
        show={!!detailTask}
        task={detailTask}
        assigneeName={detailTask ? assigneeNameFor(detailTask) : null}
        currentInfinityCode={currentInfinityCode}
        history={historyHook.history}
        historyLoading={historyHook.loading}
        styles={styles}
        screenSize={screenSize}
        t={t}
        language={language}
        onClose={() => {
          setDetailTask(null);
          historyHook.resetHistory();
        }}
        onStart={handleStart}
        onComplete={handleComplete}
        onCancelTask={handleCancelTask}
        onEdit={openEdit}
        onFetchHistory={historyHook.fetchHistory}
      />

      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        theme={theme}
        language={language}
        onClose={closeAlert}
      />
    </div>
  );
};

export default TasksSection;
